import { asArray } from "@/lib/as-array"
import type {
  AgentConversationSummary,
  AgentConversationTurn,
} from "../api/agent-conversation-client"
import type { RobinConversation, RobinMessage } from "../robin-types"
import { agentConversationToRobin, labelFromDate } from "./conversation-presenter"

export function mergeConversationList(
  current: RobinConversation[],
  backendConversations: AgentConversationSummary[],
  nowMs: number,
): RobinConversation[] {
  /*
    Spreading a non-array throws, and this one is not scoped to a page: the
    conversation rail is the app shell, so a `/agents/conversations` body that is
    not the promised shape takes the whole product down rather than one screen.
  */
  const sortedBackend = asArray<AgentConversationSummary>(backendConversations).sort(
    (a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? ""),
  )
  const backendIds = new Set(sortedBackend.map((conversation) => conversation.id))
  const currentById = new Map(current.map((conversation) => [conversation.id, conversation]))

  const localRows = current.filter((conversation) => !backendIds.has(conversation.id))
  const backendRows = sortedBackend.map((conversation) => {
    const base = agentConversationToRobin(conversation, nowMs)
    const existing = currentById.get(conversation.id)
    if (!existing) return base
    return {
      ...base,
      ...existing,
      title: conversation.title || existing.title,
      updatedLabel: labelFromDate(conversation.updated_at, nowMs),
      hasHistory: true,
      hydrated: existing.hydrated ?? false,
    }
  })

  return [...localRows, ...backendRows]
}

export function titleFrom(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim()
  return clean.length > 40 ? `${clean.slice(0, 40)}...` : clean
}

export function titleFromFiles(files: File[]): string {
  if (files.length === 1) return `Uploaded ${files[0].name || "file"}`
  return `${files.length} uploaded files`
}

export function upsertMessage(
  messages: RobinMessage[],
  message: RobinMessage,
): RobinMessage[] {
  if (messages.some((item) => item.id === message.id)) {
    return messages.map((item) => (item.id === message.id ? message : item))
  }
  return [...messages, message]
}

export function answeredAskIdFromTurn(turn: AgentConversationTurn): string | null {
  return turn.kind === "ask_user_response" ? turn.askUserToolCallId : null
}

export function resolvedChoiceIdFromTurn(
  turn: AgentConversationTurn,
): string | undefined {
  if (turn.kind !== "ask_user_response") return undefined
  return "selectedIndex" in turn.response ? String(turn.response.selectedIndex) : undefined
}
